/**
 * CurrencyChangeWizard — 3-step modal for changing the user's preferred currency.
 *
 * Step 1 (Editing): User picks a new currency from the CurrencySelect.
 * Step 2 (Preview): We hit GET /currency/convert with amount=1 to show
 *   the new rate vs the old rate. We also show "this will update N
 *   records" (N fetched from a separate /currency/convert count call
 *   or computed client-side via existing dashboard data).
 * Step 3 (Confirm): User confirms. We PUT /auth/profile with the new
 *   preferences.currency. On success, the migration service runs
 *   server-side and a toast is shown.
 *
 * Uses the existing <Dialog> primitive and useToast hook.
 *
 * @see /home/desendo/Repositories/GitHub/fintrack/.omo/plans/currency-conversion-overhaul.md §12
 */

import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import api, { endpoints } from '@/services/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CurrencySelect } from '@/components/ui/currency-select';
import { Loader2 } from 'lucide-react';

interface CurrencyChangeWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

type Step = 'editing' | 'preview' | 'submitting';

export function CurrencyChangeWizard({
  open,
  onOpenChange,
  onComplete,
}: CurrencyChangeWizardProps) {
  const { toast } = useToast();
  const { currency: currentCurrency } = useCurrency();
  const [newCurrency, setNewCurrency] = useState<string>('');
  const [step, setStep] = useState<Step>('editing');
  const [previewRate, setPreviewRate] = useState<number | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const handlePreview = async () => {
    if (!newCurrency || newCurrency === currentCurrency) return;
    setStep('preview');
    setPreviewError(null);
    try {
      // Fetch the rate for amount=1 of currentCurrency in newCurrency
      const response = await api.get(endpoints.currency.convert, {
        params: { from: currentCurrency, to: newCurrency, amount: 1 },
      });
      const data = response.data.data ?? response.data;
      setPreviewRate(typeof data?.convertedAmount === 'number' ? data.convertedAmount : null);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to fetch rate');
    }
  };

  const handleConfirm = async () => {
    if (!newCurrency || newCurrency === currentCurrency) return;
    setStep('submitting');
    try {
      // Hit PUT /auth/profile with the new currency
      await api.put(endpoints.auth.profile, {
        preferences: { currency: newCurrency },
      });
      toast({
        title: 'Currency updated',
        description: `Your records have been re-converted to ${newCurrency}.`,
      });
      onComplete?.();
      onOpenChange(false);
      // Reset wizard state for next open
      setTimeout(() => {
        setStep('editing');
        setNewCurrency('');
        setPreviewRate(null);
        setPreviewError(null);
      }, 300);
    } catch (err) {
      toast({
        title: 'Currency change failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      setStep('preview');
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep('editing');
      setNewCurrency('');
      setPreviewRate(null);
      setPreviewError(null);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {step === 'editing' && 'Change preferred currency'}
            {step === 'preview' && 'Confirm currency change'}
            {step === 'submitting' && 'Applying currency change...'}
          </DialogTitle>
          <DialogDescription>
            {step === 'editing' &&
              `Your current currency is ${currentCurrency}. Choose a new one to continue.`}
            {step === 'preview' &&
              `1 ${currentCurrency} will be revalued to approximately ${previewRate?.toFixed(4) ?? '...'} ${newCurrency}. Your records will be updated in the background.`}
            {step === 'submitting' &&
              'Re-converting your records. This may take a few seconds.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'editing' && (
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="new-currency">New currency</label>
              <CurrencySelect
                id="new-currency"
                value={newCurrency}
                onChange={setNewCurrency}
                testId="currency-change-wizard-select"
              />
            </div>
            {previewError && (
              <p className="text-sm text-destructive">{previewError}</p>
            )}
          </div>
        )}

        {step === 'preview' && (
          <div className="grid gap-4 py-4">
            <div className="rounded border p-4">
              <p className="text-sm text-muted-foreground">Current → New</p>
              <p className="text-2xl font-semibold">
                1 {currentCurrency} ≈ {previewRate?.toFixed(4) ?? '...'} {newCurrency}
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              This will update all your money records to use {newCurrency} as the
              display currency. The original amounts are preserved.
            </p>
          </div>
        )}

        {step === 'submitting' && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}

        <DialogFooter>
          {step === 'editing' && (
            <>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                disabled={!newCurrency || newCurrency === currentCurrency}
                data-testid="currency-change-wizard-preview"
              >
                Preview
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('editing')}>
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                data-testid="currency-change-wizard-confirm"
              >
                Confirm
              </Button>
            </>
          )}
          {step === 'submitting' && (
            <Button disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Applying...
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CurrencyChangeWizard;
